import { useEffect } from 'react';
import { TRACE } from '../utils/portalWordmark';

/**
 * Low-end = ≤2GB RAM or ≤4 cores. deviceMemory is Chromium-only; Safari reports
 * neither, so iPhones get the full loop (they can afford it).
 */
export function isLowEndDevice() {
  if (typeof navigator === 'undefined') return false;
  const mem = navigator.deviceMemory;
  const cores = navigator.hardwareConcurrency;
  return (!!mem && mem <= 2) || (!!cores && cores <= 4);
}

/**
 * Runs the Portal trace on every [data-trace] / [data-pulse] node under `root`,
 * which may live in another same-origin document (the New Here iframe).
 * Returns a stop function.
 *
 * Cost model — this is the whole point:
 *  - Each pass is a set of one-shot Web Animations with fill:'none'. Between passes
 *    NOTHING is animating, so the letters cost zero paint for the back half of
 *    every cycle.
 *  - The portal's swell is a transform, so it stays on the GPU.
 *  - Passes are skipped while the logo is off-screen (IntersectionObserver) or the
 *    tab is hidden.
 *  - Low-end devices get one pass on mount and no loop.
 *  - prefers-reduced-motion: no passes at all.
 * The portal's spin is plain CSS (.pr-portal-spin) and is not driven here; it runs
 * always.
 */
export function startPortalTrace(root, { reduced = false, speed = 1 } = {}) {
  if (!root || reduced || typeof root.animate !== 'function') return () => {};

  const doc = root.ownerDocument;
  const win = doc.defaultView;
  const D = TRACE.cycleMs / speed;
  let visible = true;

  // Only the animations THIS scheduler starts are ours to cancel. The portal's spin
  // is a CSS animation on the same subtree, and cancelling a CSS animation through
  // the Web Animations API stops it for good — React's dev double-mount ran this
  // cleanup once on every logo and left every O standing still.
  const spawned = new Set();
  const spawn = (el, keyframes, options) => {
    const a = el.animate(keyframes, options);
    if (a) {
      spawned.add(a);
      a.onfinish = () => spawned.delete(a);
    }
  };

  const pass = (force) => {
    if (doc.hidden || (!force && !visible)) return;
    root.querySelectorAll('[data-trace]').forEach((p) => {
      spawn(
        p,
        [{ strokeDashoffset: TRACE.dashLen }, { strokeDashoffset: -100 }],
        { duration: TRACE.sweep * D, delay: Number(p.dataset.trace) * TRACE.step * D, easing: 'linear', fill: 'none' },
      );
    });
    // The portal swells as the streak reaches its slot (slot 1). Transform only.
    root.querySelectorAll('[data-pulse]').forEach((r) => {
      spawn(
        r,
        [{ transform: 'scale(1)' }, { transform: 'scale(1.08)', offset: 0.3 }, { transform: 'scale(1)' }],
        { duration: TRACE.pulse * D, delay: TRACE.step * D, easing: 'ease-out', fill: 'none' },
      );
    });
  };

  // With several lockups under one root (the landing has three) the observer only
  // says whether the root is on screen; that is the page, and good enough.
  const io = typeof win?.IntersectionObserver === 'function'
    ? new win.IntersectionObserver(([e]) => { visible = e.isIntersecting; })
    : null;
  io?.observe(root);

  const first = setTimeout(() => pass(true), 300);
  const loop = isLowEndDevice() ? null : setInterval(() => pass(false), D);

  return () => {
    clearTimeout(first);
    if (loop) clearInterval(loop);
    io?.disconnect();
    spawned.forEach((a) => a.cancel());
    spawned.clear();
  };
}

/** The same, bound to a React ref. */
export default function usePortalTrace(rootRef, { reduced = false, speed = 1 } = {}) {
  useEffect(() => startPortalTrace(rootRef.current, { reduced, speed }), [rootRef, reduced, speed]);
}
